/**
 * Staff idle sign-out on the screen (PM aa0f43a7).
 *
 * The club sets how many minutes a signed-in staff screen may sit untouched
 * (per staff member, or a club default). This watches for a person touching
 * the screen in any open tab (shared through localStorage, see
 * api/staffIdleSession), warns a minute before the limit, and signs the screen
 * out when it passes, returning to the sign-in page with a message.
 *
 * The backend enforces the same limit on every request (401
 * STAFF_IDLE_TIMEOUT), so a screen whose page was asleep is refused on its
 * next request even before this ticks. Sessions with no limit (families, and
 * staff when the club has set nothing) render nothing and do nothing.
 *
 * Mounted once from App, outside every layout.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../ui";
import { getAuthToken, getTenantId } from "../../api/apiHelpers";
import { endSessionForIdle } from "../../api/axiosInstance";
import { getIdleLimitMinutes, idleSeconds, markActivity } from "../../api/staffIdleSession";

/** How long before the limit the warning shows. */
export const IDLE_WARNING_SECONDS = 60;
const TICK_MS = 1000;
/** A person moving the mouse fires many events a second; one write a second is plenty. */
const MARK_EVERY_MS = 1000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll", "mousemove"] as const;

const StaffIdleSignOut: React.FC = () => {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const lastMark = useRef(0);
  const nearLimit = useRef(false);
  /** Signed out once; the redirect is under way, so never again from this tab. */
  const signedOut = useRef(false);

  const onActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastMark.current < MARK_EVERY_MS) return;
    lastMark.current = now;
    markActivity(now);
  }, []);

  useEffect(() => {
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, { passive: true, capture: true }));

    const tick = () => {
      if (signedOut.current) return;
      const limit = getIdleLimitMinutes();
      if (limit === null) {
        // Another tab signed this screen out at the limit: follow it to the
        // sign-in page instead of sitting on a page with no session.
        if (nearLimit.current && !getAuthToken()) {
          nearLimit.current = false;
          signedOut.current = true;
          const slug = getTenantId();
          window.location.assign(slug ? `/${slug}/login?sessionExpired=idle` : "/login?sessionExpired=idle");
        }
        setSecondsLeft(null);
        return;
      }
      const idle = idleSeconds();
      if (idle === null) {
        setSecondsLeft(null);
        return;
      }
      const left = limit * 60 - idle;
      if (left <= 0) {
        nearLimit.current = false;
        signedOut.current = true;
        setSecondsLeft(null);
        endSessionForIdle();
        return;
      }
      nearLimit.current = left <= IDLE_WARNING_SECONDS;
      setSecondsLeft(left <= IDLE_WARNING_SECONDS ? left : null);
    };

    tick();
    const timer = window.setInterval(tick, TICK_MS);
    return () => {
      window.clearInterval(timer);
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity, { capture: true }));
    };
  }, [onActivity]);

  if (secondsLeft === null) return null;

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      aria-labelledby="staff-idle-title"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[10000] w-[calc(100%-2rem)] max-w-md rounded-lg border border-gray-200 bg-white p-4 shadow-lg dark:border-gray-700 dark:bg-gray-800"
    >
      <p id="staff-idle-title" className="font-semibold text-gray-900 dark:text-gray-100">
        Still there?
      </p>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
        This screen will sign out in {secondsLeft} second{secondsLeft === 1 ? "" : "s"} because it has not been used.
      </p>
      <div className="mt-3 flex justify-end">
        <Button
          variant="primary"
          size="md"
          onClick={() => {
            lastMark.current = Date.now();
            markActivity(lastMark.current);
            setSecondsLeft(null);
          }}
        >
          Stay signed in
        </Button>
      </div>
    </div>
  );
};

export default StaffIdleSignOut;
